import React from 'react';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { getCargoColor } from 'src/utils/stowage/cargoColorsUtils';
import { getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';

/**
 * Creates a custom drag image that mimics the CargoNumber component appearance
 * for drag-and-drop operations. The image includes the operation icon and cargo number
 * with consistent styling.
 *
 * @param cargoOperation - The cargo operation being dragged
 * @param event - The drag event
 */
export const createCustomDragImage = (cargo: RotationCargo, operationType: string, event: React.DragEvent): void => {
    // Create a temporary container for rendering the drag image
    const dragImageContainer = document.createElement('div');
    dragImageContainer.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        opacity: 0.8;
        z-index: 9999;
    `;

    // Get operation icon based on operation type
    const getOperationIcon = (operationType: string): string => {
        switch (operationType) {
            case 'Loading':
                return '📥';
            case 'Unloading':
                return '📤';
            case 'Transfer':
            case 'Transit':
                return '🔄';
            default:
                return '📋';
        }
    };

    // Get cargo number text using the same logic as CargoNumber component
    const getCargoNumberText = (): string => {
        const isManual = isManualCargo(cargo);
        const cargoNumberText = getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, isManual);
        return `#${cargoNumberText}`;
    };

    const operationIcon = getOperationIcon(operationType);
    const cargoNumberDisplayText = getCargoNumberText();
    const cargoColor = getCargoColor(cargo);

    // Create the drag image HTML with styling that matches CargoNumber component
    dragImageContainer.innerHTML = `
        <div style="display: inline-flex; align-items: center; gap: 8px;">
            <span style="font-size: 30px;">${operationIcon}</span>
            <div style="
                background: #${cargoColor};
                color: #333;
                padding: 1px 4px;
                border-radius: 10px;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 14px;
                font-weight: 600;
                white-space: nowrap;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                border: 1px solid #ddd;
            ">${cargoNumberDisplayText}</div>
        </div>
    `;

    // Add to document temporarily
    document.body.appendChild(dragImageContainer);

    // Set as drag image with offset to appear below the cursor
    event.dataTransfer.setDragImage(dragImageContainer, 70, -15);

    // Remove the temporary element after a short delay
    setTimeout(() => {
        if (document.body.contains(dragImageContainer)) {
            document.body.removeChild(dragImageContainer);
        }
    }, 100);
};
